// Compare actual values rather than input events: nested hidden destroy fields,
// files and programmatic rich-text edits must all participate in the guard.
export default class UnsavedChanges {
  constructor(form) {
    this.form = form
    this.baseline = this.snapshot()
    this.listeners = []
    this.listen(window, "beforeunload", event => {
      if (this.leaving || !this.dirty) return
      event.preventDefault()
      event.returnValue = ""
    })
    this.listen(window, "pageshow", () => { this.leaving = false })
    this.listen(document, "click", event => this.navigate(event), true)
    this.listen(document, "submit", event => this.submit(event), true)
    this.listen(form, "form:saving", () => {
      this.pending = this.snapshot()
      // Active Storage has replaced selected files with signed hidden inputs.
      // The file input itself is cleared after the request, also on failure.
      for (const field of this.pending.keys()) {
        if (field.type === "file" && field.disabled) this.pending.set(field, "")
      }
    })
    this.listen(form, "form:saved", () => {
      // Keep edits made while the request was in flight dirty. Server-assigned
      // IDs are bookkeeping; removed persisted audit rows no longer exist.
      this.baseline = new Map(Array.from(this.pending).filter(([field]) => form.contains(field)))
    })
    this.listen(form, "editor:initialized", event => {
      // TinyMCE normalizes HTML on initialization; update only that field.
      if (this.baseline.has(event.target)) this.baseline.set(event.target, this.value(event.target))
    })
  }

  listen(target, type, handler, capture = false) {
    target.addEventListener(type, handler, capture)
    this.listeners.push(() => target.removeEventListener(type, handler, capture))
  }

  disconnect() {
    this.listeners.forEach(remove => remove())
    if (this.dialog) this.dialog.remove()
  }

  value(field) {
    if (field.type === "file") return Array.from(field.files).map(file => [file.name, file.size, file.lastModified]).toString()
    if (field.type === "checkbox" || field.type === "radio") return `${field.value}:${field.checked}`
    const editor = window.tinymce && window.tinymce.get(field.id)
    if (editor && editor.initialized) return editor.getContent()
    if (field.multiple) return Array.from(field.selectedOptions).map(option => option.value).toString()
    return field.value
  }

  snapshot() {
    return new Map(Array.from(this.form.elements)
      .filter(field => field.name && !["submit", "button"].includes(field.type) &&
        !(field.type === "hidden" && (field.name === "authenticity_token" || field.name === "id" || field.name.endsWith("[id]"))))
      .map(field => [field, this.value(field)]))
  }

  get dirty() {
    const current = this.snapshot()
    return current.size !== this.baseline.size || Array.from(current).some(([field, value]) => this.baseline.get(field) !== value)
  }

  navigate(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.getAttribute("href").startsWith("#") || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
        link.hasAttribute("download") || (link.target && link.target !== "_self")) return
    const url = new URL(link.href)
    if (url.searchParams.get("disposition") === "attachment" || !["http:", "https:"].includes(url.protocol) ||
        (url.pathname === location.pathname && url.search === location.search && url.hash)) return
    if (!this.dirty) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.confirmLeave(() => {
      this.leaving = true
      window.location.assign(url.href)
    })
  }

  submit(event) {
    // Saving this form is handled by its existing async controller. Other
    // submissions (including final submission/logout) can discard these edits.
    if (event.target === this.form || this.allowSubmit || !this.dirty) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const form = event.target
    const submitter = event.submitter
    this.confirmLeave(() => {
      this.allowSubmit = true
      let resumed
      const remember = event => { resumed = event }
      form.addEventListener("submit", remember, { once: true })
      form.requestSubmit(submitter)
      form.removeEventListener("submit", remember)
      this.allowSubmit = false
      // Existing confirmation can still cancel; retain the dirty state then.
      this.leaving = resumed && !resumed.defaultPrevented
    })
  }

  confirmLeave(proceed) {
    if (this.dialog) return
    const previousFocus = document.activeElement
    const dialog = document.createElement("dialog")
    this.dialog = dialog
    dialog.className = "ui-dialog"
    dialog.setAttribute("aria-labelledby", "unsaved-title")
    dialog.setAttribute("aria-describedby", "unsaved-description")
    dialog.innerHTML = `
      <h2 id="unsaved-title">Nesaglabātas izmaiņas</h2>
      <p id="unsaved-description">Šajā solī ir nesaglabātas izmaiņas. Atstājot lapu, tās tiks zaudētas. Lai tās saglabātu, turpini rediģēt un nospied “Saglabāt”.</p>
      <div class="ui-dialog__actions">
        <button type="button" class="btn btn-primary" autofocus>Turpināt rediģēt</button>
        <button type="button" class="btn btn-outline-secondary" data-leave>Atstāt bez saglabāšanas</button>
      </div>`
    const close = () => {
      dialog.close()
      dialog.remove()
      this.dialog = null
      if (previousFocus) previousFocus.focus()
    }
    dialog.addEventListener("cancel", event => { event.preventDefault(); close() })
    dialog.querySelector("button").addEventListener("click", close)
    dialog.querySelector("[data-leave]").addEventListener("click", () => { close(); proceed() })
    document.body.appendChild(dialog)
    dialog.showModal()
  }
}
